import * as Sentry from '@sentry/react';
import type { QueryClient } from '@tanstack/react-query';
import { redirect } from '@tanstack/react-router';
import { ApiError } from './api.ts';
import { type CurrentUser, fetchCurrentUser } from './auth.ts';
import { leaveTo } from './leaveTo.ts';

/*
 * What the two route guards decide, and why. One asks "may this person be
 * here", the other "is this person already in" — and both hang on telling an
 * answer of "no" apart from no answer at all.
 */

/**
 * Loading the person *is* the session check, so this is one request rather than
 * two. It also primes the cache the shell is about to read, so the rail renders
 * with a name instead of waiting on a second round trip.
 *
 * It is also the one place every signed-in visit passes through, so this is
 * where Sentry learns who the session belongs to — the id and nothing else, so
 * replays and errors can be tied to a user without putting a name or address in
 * them. A change of identity forces a full reload, so setting it once per page
 * load is enough.
 */
function ensureCurrentUser(queryClient: QueryClient): Promise<CurrentUser> {
  return queryClient
    .ensureQueryData({
      queryKey: ['currentUser'],
      queryFn: fetchCurrentUser,
    })
    .then((user) => {
      Sentry.setUser({ id: user.id });

      return user;
    });
}

/**
 * Whether a failed session load was an answer of "no", rather than no answer.
 *
 * Only 401 and 403 mean the person is not signed in. A backend that cannot be
 * reached, or that answered 500, refused nothing. A failed check is not a
 * verdict.
 */
export function isSessionRefusal(caught: unknown): boolean {
  return caught instanceof ApiError && caught.type === 'unauthorized';
}

/** The backend's code for a session whose organisation is not on the web app. */
export const WEB_APP_UNAVAILABLE_CODE = 'web_app_unavailable';

/**
 * Signed in, but to an organisation off the web app: the session opens a
 * practice and nothing else, so the app has nothing to show them.
 */
export function isWebAppUnavailable(caught: unknown): boolean {
  return caught instanceof ApiError && caught.code === WEB_APP_UNAVAILABLE_CODE;
}

/* Beside the login screen, on the customer app, whichever app is asking. */
const UNAVAILABLE_PATH = '/unavailable';

/*
 * The router leaves the document for an absolute `href` and routes a
 * root-relative one, so one redirect serves both apps.
 */
function redirectToUnavailable(loginUrl: string): never {
  if (isRootRelative(loginUrl)) {
    throw redirect({ href: UNAVAILABLE_PATH, replace: true });
  }

  throw redirect({ href: new URL(UNAVAILABLE_PATH, loginUrl).toString() });
}

export interface RequireSessionOptions {
  /**
   * The login screen to send a refused visitor to. Root-relative for an app
   * that hosts its own; absolute for one that borrows another app's.
   *
   * Only one login screen exists, and the customer app is where it lives, so
   * the staff console has to name it. There is no deriving one origin from the
   * other: the two are siblings in production (admin.wundo.ai / app.wundo.ai)
   * and not on stage (admin.stage.wundo.ai / stage.wundo.ai), so it is
   * configured rather than computed.
   */
  loginUrl?: string;
}

const DEFAULT_LOGIN_URL = '/login';

/**
 * Whether an address is inside this app, rather than somewhere the browser has
 * to be handed over to.
 *
 * `//host/path` is excluded deliberately: it starts with a slash and is an
 * address on another origin regardless, so reading it as a local path would
 * turn a foreign login screen into a route this app does not have.
 */
function isRootRelative(url: string): boolean {
  return url.startsWith('/') && !url.startsWith('//');
}

/**
 * The guard on the signed-in half of the app. Returns the person for anyone
 * with a session; everyone else is sent to the login screen.
 *
 * Where they were headed travels with them, absolute because the backend
 * checks a return address against the frontends it serves and reads a relative
 * one as naming none. Links arriving from chat are the reason it has to: they land deep in
 * the app, and a sign-in that forgets the destination answers "connect your
 * calendar" with the home page.
 *
 * Only a refusal sends them there. An outage is rethrown, so the app says the
 * backend is unreachable rather than logging everyone out on our side — which
 * would blame the person's session for someone else's failure, and hand them
 * a login screen that cannot reach the backend either.
 *
 * How they are sent follows from where the screen is. A root-relative one is a
 * route in this app, so it travels as `to` and the router navigates to it
 * without reloading. An absolute one is outside the router's world — `to`
 * would read it as a path on this origin — so it travels as `href`, which the
 * router recognises as external and leaves the document for.
 *
 * Both exits throw, so a loader stops where it stands either way. Returning
 * once the browser was on its way out would let a protected route render to
 * somebody with no session in the meantime, and leave every caller to defend
 * against a guard that had already said no.
 */
export async function requireSession(
  queryClient: QueryClient,
  href: string,
  options: RequireSessionOptions = {},
): Promise<CurrentUser> {
  try {
    return await ensureCurrentUser(queryClient);
  } catch (caught) {
    if (!isSessionRefusal(caught)) {
      throw caught;
    }

    const loginUrl = options.loginUrl ?? DEFAULT_LOGIN_URL;

    /* Signing in again would only end in the same refusal. */
    if (isWebAppUnavailable(caught)) {
      redirectToUnavailable(loginUrl);
    }
    const returnTo = `${window.location.origin}${href}`;

    if (!isRootRelative(loginUrl)) {
      const login = new URL(loginUrl);
      login.searchParams.set('redirect', returnTo);

      /*
       * An absolute `href` is the router's own way of naming somewhere it
       * cannot route to: it infers a full-document navigation from it and
       * performs the leaving itself, so this stays one thrown redirect like
       * the branch below rather than a navigation plus a stop.
       */
      throw redirect({ href: login.toString() });
    }

    /*
     * `to` names a route of whichever app is asking, and the router types it
     * against that app's registered route tree — which this package, compiled
     * inside each consumer in turn, cannot know. The staff console borrows the
     * customer app's login screen and always passes an absolute URL, so it has
     * no `/login` route of its own for this string to match, and the branch is
     * unreachable there. Hence the cast: the alternative is a shared guard that
     * only compiles in the one app that happens to host the screen.
     */
    throw redirect({
      to: loginUrl,
      search: {
        redirect: returnTo,
        error: undefined,
      },
    } as unknown as Parameters<typeof redirect>[0]);
  }
}

/**
 * The guard on the login screen: someone who already has a session is sent on
 * to `destination` rather than asked to sign in again. Answers whether they
 * were, so the screen can hold the form back while the browser leaves.
 *
 * Reloading is then a way out of that screen instead of a repeat of it, which
 * matters most right after an outage — that is how someone perfectly signed in
 * ends up looking at it.
 *
 * The fallback is the other way round from the guard above: if the backend
 * cannot say, the form renders, because that is what someone without a session
 * needs and the only thing this screen has to offer.
 */
export async function redirectIfSignedIn(
  queryClient: QueryClient,
  destination: string,
): Promise<boolean> {
  try {
    await ensureCurrentUser(queryClient);
  } catch (caught) {
    if (!isWebAppUnavailable(caught)) {
      return false;
    }

    /* Signed in all the same, to a session with no app behind it. */
    leaveTo(UNAVAILABLE_PATH);

    return true;
  }

  leaveTo(destination);

  return true;
}
